import { Suspense, useState } from "react";
import { IoSearch } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import type { ChecklistsResponse } from "~/data/admin/checklist/checklist.dto";

import { Input } from "~/ui/shared/input";
import { Button } from "~/ui/shared/button";
import { useIntesectionObserver } from "~/ui/shared/utils/use-intersection-observer";
import {
  createChecklist,
  getChecklists,
} from "~/data/admin/checklist/checklist.service";

export function Checklists() {
  const navigate = useNavigate();
  const newChecklistMutation = useMutation({
    mutationFn: () => createChecklist(),
  });
  const [search, setSearch] = useState("");

  return (
    <div className="p-10">
      <header className="mb-5 flex justify-between items-center py-2">
        <div>
          <h2 className="text-3xl font-semibold mb-1">Gerenciar Checklists</h2>
        </div>

        <Button
          variant="primary-1"
          className="h-14 w-80"
          onClick={() =>
            newChecklistMutation.mutate(void 0, {
              onSuccess: (data) => navigate(data.id),
            })
          }
          loading={newChecklistMutation.isPending}
        >
          Novo Checklist
        </Button>
      </header>

      <div className="mb-5">
        <div className="w-max ml-auto">
          <div className="relative w-64">
            <Input
              type="search"
              onChange={(e) => setTimeout(() => setSearch(e.target.value), 400)}
              placeholder="Buscar checklist"
              className="pl-10"
            />
            <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-5 w-5" />
          </div>
        </div>
      </div>

      <ErrorBoundary fallback={<p>Erro ao carregar dados</p>}>
        <Suspense
          fallback={
            <div className="grid gap-4">
              {Array.from({ length: 10 }, (_, i) => (
                <div
                  key={String(i)}
                  className="rounded-xl h-14 bg-gray-100 animate-pulse"
                />
              ))}
            </div>
          }
        >
          <ChecklistsList search={search} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function ChecklistsList({ search }: { search: string }) {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery({
    queryKey: ["admin_checklists", search],
    queryFn: () => getChecklists({ query: search }),
    select: (data) => ({ rows: data.data, next: data.meta.next_page }),
  });
  const loadMoreMutation = useMutation({
    mutationFn: (next: number) => getChecklists({ query: search, page: next }),
    onSuccess: (data) =>
      queryClient.setQueryData<ChecklistsResponse>(
        ["admin_checklists", search],
        (state) => state && { ...data, data: [...state.data, ...data.data] }
      ),
  });
  const ref = useIntesectionObserver<HTMLLIElement>(() => {
    if (loadMoreMutation.isPending || !data.next) return;
    loadMoreMutation.mutate(data.next);
  });
  return (
    <ul className="grid gap-4">
      {data.rows.map((it, i, self) => (
        <li
          key={it.id}
          className="grid bg-[#FBFBFB] hover:bg-gray-100 rounded-xl border-l-[6px] border-primary-1"
          style={{ boxShadow: "0px 10px 20px 0px #00000014" }}
          ref={self.length - 1 === i ? ref : void 0} // assign ref if is last
        >
          <Link className="py-4 px-6" to={`/checklists/${it.id}`}>
            {it.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}
